import { BadgeColours, GisDatenVHV, GisDatenVWG, IPriceable } from '@ps20-frontend/frontend-models';
import { SummenWerte, TkvRadio, ZusatzoptionenDruck } from '@ps20/bff-models';

export interface IncludedLeistung {
  fachschluessel: string;
  textId: string;
  leistung: string;
  reihenfolge: number;
}

export interface ZusatzoptionenSchadenbeispiele {
  link?: string;
  title?: string;
  reference?: string;
  left?: ZusatzoptionenSchadenbeispieleLeftRight;
  right?: ZusatzoptionenSchadenbeispieleLeftRight;
}

export interface ZusatzoptionenSchadenbeispieleLeftRight {
  headline?: string;
  subheadline?: string;
}

export interface ZusatzoptionenBesonderheiten {
  link?: string;
  closeButton?: string;
  text?: string;
}

export interface Extremwetterschutz {
  text: string;
  info: string;
  durchschnittlich: string;
  erhoeht: string;
  gravierend: string;
}

export interface ZusatzoptionItem extends IPriceable {
  id: string | null;
  readonly name: string;
  readonly info: string;
  bruttopreis: number;
  nettopreisProJahr: number;
  isSelected: boolean;
  readonly isInclusive: boolean;
  readonly visible?: boolean;
  fachschluessel?: string;
  reihenfolge?: number;
  initialSelektiert?: boolean;
  writeToEinreichenXML?: boolean;
  includedLeistungen?: IncludedLeistung[];
  eigenschaft?: string;
  summenWerte?: SummenWerte;
  summenBezeichnung?: string;
  deltafaktor?: number;
  plzFaktor?: number;
  anzahlVorschaeden?: number;
  disabled?: boolean;
  leistung?: string;
  bausteinAnpassungsfaktor?: number;
  hinweis?: string;
  kaufpreis?: number;
  kaufdatum?: string;
  vermietetesItem?: VermietetesItem;
  icon: string;
  title: string;
  beschreibung: string;
  anzahlWohnungen: string;
  header?: string;
  subtitle?: string;
  label?: string;
  titleprint?: string;
  tkvRadio?: TkvRadio;
  besonderheiten?: ZusatzoptionenBesonderheiten;
  schadenbeispiele?: ZusatzoptionenSchadenbeispiele;
  disabledLabel?: string;
  extremwetterschutz?: Extremwetterschutz;
  entschaedigungsgrenze?: number;
  einzelwert?: number;
}

export interface VermietetesItem {
  adresse: string;
  wohneinheiten?: number;
  gewerbeEinheiten?: number;
  stellplaetze?: number;
  flurstueckNummer?: string;
  flaeche?: number;
  bruttoJahremiete?: number;
}

export const ZUSATZOPTION_ID_TRAGERISIKO = 'tragerisiko';
export const WEITERE_NATURGEFAHREN_ID = 'weitereNaturgefahren';

export function mapZusatzoptionenDruck(
  zusatzoptionItems: ZusatzoptionItem[],
): Array<ZusatzoptionenDruck> {
  return zusatzoptionItems.map(zusatzoptionen => {
    const zusatzoptionenDruck: ZusatzoptionenDruck = {
      id: zusatzoptionen.id,
      gewaehlt: zusatzoptionen.isSelected,
      betrag:
        zusatzoptionen.bruttopreis != null
          ? numberToGermanNumberFormattedString(zusatzoptionen.bruttopreis)
          : '',
      eigenschaft: zusatzoptionen.eigenschaft,
    };
    if (zusatzoptionen.id === ZUSATZOPTION_ID_TRAGERISIKO) {
      zusatzoptionenDruck.entschaedigung = numberToGermanNumberFormattedString(
        zusatzoptionen.entschaedigungsgrenze,
      );
      zusatzoptionenDruck.einzelwert = numberToGermanNumberFormattedString(
        zusatzoptionen.einzelwert,
      );
    }
    return zusatzoptionenDruck;
  });
}

export function numberToGermanNumberFormattedString(num: number): string {
  return num?.toFixed(2)?.replace('.', ',');
}

export function getBadgeType(gisDaten: GisDatenVWG | GisDatenVHV): string {
  const risikoklasse = calculateRisikoklasse(gisDaten);
  if (risikoklasse < 1.5) {
    return BadgeColours.GREEN;
  } else if (risikoklasse >= 1.5 && risikoklasse < 2.5) {
    return BadgeColours.YELLOW;
  } else if (risikoklasse >= 2.5) {
    return BadgeColours.RED;
  }
  return undefined;
}

export function getBadgeText(
  gisDaten: GisDatenVWG | GisDatenVHV,
  extremwetterschutz: Extremwetterschutz,
): string {
  const risikoklasse = calculateRisikoklasse(gisDaten);
  if (risikoklasse < 1.5) {
    return extremwetterschutz.durchschnittlich;
  } else if (risikoklasse >= 1.5 && risikoklasse < 2.5) {
    return extremwetterschutz.erhoeht;
  } else if (risikoklasse >= 2.5) {
    return extremwetterschutz.gravierend;
  }
}

export function calculateRisikoklasse(gisDaten: GisDatenVWG | GisDatenVHV): number {
  const zuers_gefaehrdungsklasse = gisDaten?.zuersGefaehrdungsklasse;
  const senke = gisDaten?.senke;
  const starkregenzone = gisDaten?.starkregenzone;
  const bachzone = gisDaten?.bachzone;

  if (zuers_gefaehrdungsklasse === 4) {
    return 3;
  }

  if (zuers_gefaehrdungsklasse === 3) {
    return (
      (zuers_gefaehrdungsklasse * 0.9 + senke * 0.1) * 0.9 +
      (starkregenzone * 0.7 + bachzone * 0.3) * 0.1
    );
  }

  if (zuers_gefaehrdungsklasse === 2) {
    return (
      (zuers_gefaehrdungsklasse * 0.9 + senke * 0.1) * 0.3 +
      (starkregenzone * 0.7 + bachzone * 0.3) * 0.7
    );
  }

  if (zuers_gefaehrdungsklasse === 1) {
    return (
      (zuers_gefaehrdungsklasse * 0.9 + senke * 0.1) * 0.1 +
      (starkregenzone * 0.7 + bachzone * 0.3) * 0.9
    );
  }

  if (starkregenzone === 3) {
    return starkregenzone * 0.7 + bachzone * 0.3;
  }

  return null;
}
